import React from "react";
import { Link } from "react-router-dom";
import useSEO from "@/hooks/useSEO";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import {
  ArrowUpRight,
  FileSearch,
  Undo2,
  Layers,
  ClipboardCheck,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { LogoMark } from "@/components/AppLayout";

export default function Landing() {
  // Without this, SPA-navigating back from a tool/blog/glossary page leaves
  // that page's title, canonical, and OG tags in place.
  useSEO({
    title: "ProofMark — AI Knowledge Governance with a Full Audit Trail",
    description:
      "ProofMark governs what operational knowledge enters enterprise AI — review workflow, evidence chain, and revocation for every artifact. Built for compliance-aware ops teams.",
    path: "/",
  });
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      <TopNav />
      <Hero />
      <LogosRow />
      <AnchorStats />
      <Problem />
      <ThreeClasses />
      <HowItWorks />
      <FlagshipScreens />
      <BuyerValue />
      <MidCTA />
      <Architecture />
      <WhatWeAreNot />
      <Integrations />
      <Security />
      <FreeTools />
      <FAQ />
      <CTA />
      <Footer />
    </div>
  );
}

function TopNav() {
  return (
    <div className="sticky top-0 z-20 border-b border-border/80 bg-background/90 backdrop-blur">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
          <LogoMark />
          <span className="font-serif-display text-lg">ProofMark</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-foreground/80">
          <a href="#how" className="hover:text-foreground">How it works</a>
          <a href="#classes" className="hover:text-foreground">Knowledge classes</a>
          <a href="#buyers" className="hover:text-foreground">For ops &amp; compliance</a>
          <a href="#security" className="hover:text-foreground">Security</a>
          <Link to="/tools" className="hover:text-foreground">Free tools</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
        </nav>
        <div className="flex items-center gap-2">
          <a
            href="#cta"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-md border border-border px-3.5 py-2 text-sm hover:bg-muted transition-colors"
          >
            Request access
          </a>
          <Link
            to="/app"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-3.5 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
            data-testid="landing-open-app"
          >
            Open app <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border/80">
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div className="relative max-w-[1240px] mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" />
            AI knowledge governance · Now in early access
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] mt-6">
            The knowledge your AI learned last week — <em className="italic text-bronze">can you prove it was reviewed?</em>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-foreground/75 max-w-2xl leading-relaxed">
            Most AI governance frameworks cover the model. None cover what the model was allowed to learn.
            ProofMark fills that gap — with a review workflow, evidence chain, and revocation system
            for every knowledge artifact your AI acts on.
          </p>
          <div className="mt-10 max-w-sm" data-testid="hero-cta-access">
            <EarlyAccessForm source="landing-hero" buttonText="Request early access" />
            <p className="mt-2 text-xs text-muted-foreground">No spam. Unsubscribe any time.</p>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Link
              to="/tools/policy-checker"
              className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-3 text-sm hover:bg-muted transition-colors"
              data-testid="hero-cta-tool"
            >
              Check an artifact free <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href="#how"
              className="inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="hero-cta-how"
            >
              See the workflow →
            </a>
          </div>
        </div>

        <HeroArtifactPreview />
      </div>
    </section>
  );
}

function HeroArtifactPreview() {
  return (
    <div className="relative mt-16 md:mt-20 grid md:grid-cols-12 gap-6">
      <div className="md:col-span-7 rounded-lg border border-border bg-card shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/80">
          <div className="flex items-center gap-3">
            <span className="tracked-caps">Candidate artifact</span>
            <span className="font-mono text-xs text-muted-foreground">CA-2041</span>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[11px] font-mono uppercase tracking-wider bg-amber-50 text-amber-800 border-amber-200">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-600" /> In review
          </span>
        </div>
        <div className="p-5">
          <h3 className="font-serif-display text-xl leading-snug">
            Escalation rule for payment failure after 3DS verification
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Synthesized from 146 resolved cases · EU Payments
          </p>
          <div className="mt-4 rounded-md bg-muted/40 p-4 font-mono text-[13px] leading-relaxed text-foreground/90 whitespace-pre-wrap">
{`WHEN a payment fails AFTER successful 3DS AND reason is 'acquirer_decline'
THEN route to Tier 2 Payments with evidence bundle
     (3DS result, acquirer code, country, issuer BIN)
AGENT MUST NOT initiate a refund.
SLA  first response 30m · resolution 4h`}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-wider">
            <span className="rounded border border-amber-200 bg-amber-50 text-amber-900 px-2 py-0.5">Class 2 · 146</span>
            <span className="rounded border border-rose-200 bg-rose-50 text-rose-800 px-2 py-0.5">Policy flag · PII-sensitive</span>
            <span className="rounded border border-border bg-card px-2 py-0.5 text-muted-foreground">Target · EU Payments Playbook</span>
          </div>
        </div>
      </div>

      <div className="md:col-span-5 space-y-4">
        <div className="rounded-lg border border-border bg-card shadow-sm p-5">
          <div className="tracked-caps">Evidence record</div>
          <div className="mt-2 font-mono text-xs text-muted-foreground">EV-10177</div>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-center justify-between"><span className="text-muted-foreground">Source system</span><span>Zendesk</span></li>
            <li className="flex items-center justify-between"><span className="text-muted-foreground">Source IDs</span><span className="font-mono text-xs">5 tickets</span></li>
            <li className="flex items-center justify-between"><span className="text-muted-foreground">Policy basis</span><span>PII redaction applied</span></li>
            <li className="flex items-center justify-between"><span className="text-muted-foreground">Review chain</span><span>A. Cho → M. Okafor</span></li>
            <li className="flex items-center justify-between"><span className="text-muted-foreground">Completeness</span><span className="font-mono">65%</span></li>
          </ul>
        </div>
        <div className="rounded-lg border border-border bg-card shadow-sm p-5">
          <div className="tracked-caps">Decision</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button className="rounded-md bg-brand text-brand-foreground py-2 text-sm font-medium">Approve</button>
            <button className="rounded-md border border-border py-2 text-sm">Approve w/ edits</button>
            <button className="rounded-md border border-border py-2 text-sm">Send back</button>
            <button className="rounded-md border border-border py-2 text-sm">Dispute</button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Every decision writes to the evidence record. Publication happens only on explicit approval.
          </p>
        </div>
      </div>
    </div>
  );
}

function LogosRow() {
  const companies = ["ATLAS BANK", "MERIDIAN", "NORTH STAR LOGISTICS", "VERIDIS", "KIRBY & SONS", "IONIC"];
  return (
    <section className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-10">
        <div className="tracked-caps text-center">Used by Heads of AI, VP Ops, and Chief Compliance Officers at regulated enterprises building AI into customer operations</div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-8 items-center">
          {companies.map((c) => (
            <div key={c} className="text-center font-serif-display text-sm md:text-base text-foreground/40">{c}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="tracked-caps">The problem</div>
          <h2 className="mt-3 font-serif-display text-4xl md:text-5xl leading-tight">
            Your recurring operational knowledge is already in AI.<br />
            The question is whether it should be.
          </h2>
        </div>
        <div className="md:col-span-7 space-y-6 text-[17px] leading-relaxed text-foreground/80">
          <p>
            Every month, thousands of resolved support cases, playbook edits, and macro tweaks
            solidify into patterns your teams depend on. Most enterprises have no record of
            which of those patterns are safe to operationalize — or which contributor class
            they were derived from.
          </p>
          <p>
            ProofMark is not a knowledge base. It is the governed pipeline between human work
            and enterprise AI behavior: candidate extraction, policy-aware review, explicit
            publication, and revocation.
          </p>
        </div>
      </div>
    </section>
  );
}

function ThreeClasses() {
  const items = [
    {
      tag: "Class 1",
      title: "Explicit organizational knowledge",
      body: "Published playbooks, KB articles, SOPs. Company-owned, low-ambiguity. Fast path to approval.",
      tone: "border-emerald-200 bg-emerald-50/40",
    },
    {
      tag: "Class 2",
      title: "Traceable derived operational knowledge",
      body: "Patterns synthesized across many cases. Must be extracted into candidate artifacts, reviewed, and republished.",
      tone: "border-amber-200 bg-amber-50/40",
      emphasis: true,
    },
    {
      tag: "Class 3",
      title: "Implicit individual cognitive patterns",
      body: "Out of scope. ProofMark never extracts, models, or operationalizes an individual's implicit cognition.",
      tone: "border-rose-200 bg-rose-50/40",
    },
  ];
  return (
    <section id="classes" className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
        <div className="tracked-caps">What we govern</div>
        <h2 className="mt-3 font-serif-display text-4xl md:text-5xl leading-tight max-w-3xl">
          Three classes of knowledge. ProofMark is built for the middle one.
        </h2>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {items.map((it) => (
            <article key={it.tag} className={`rounded-lg border ${it.tone} p-7 ${it.emphasis ? "md:-translate-y-2 shadow-md" : ""} transition-all`}>
              <div className="flex items-center justify-between">
                <span className="tracked-caps">{it.tag}</span>
                {it.emphasis && <span className="rounded-full bg-brand text-brand-foreground text-[10px] font-mono uppercase tracking-wider px-2 py-0.5">Product core</span>}
              </div>
              <h3 className="mt-3 font-serif-display text-2xl leading-tight">{it.title}</h3>
              <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{it.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", icon: Layers, title: "Observe patterns", body: "ProofMark watches resolved cases, macro edits, and KB deltas — strictly within policy scope." },
    { n: "02", icon: FileSearch, title: "Extract candidate artifact", body: "Recurring patterns become typed drafts: escalation rules, decision trees, macros, onboarding updates." },
    { n: "03", icon: ClipboardCheck, title: "Human review", body: "A reviewer opens a 3-pane workspace with source lineage, policy flags, and an editable draft." },
    { n: "04", icon: ShieldCheck, title: "Publish to target", body: "On explicit approval, the artifact is published to its target playbook, KB, or AI retrieval index." },
    { n: "05", icon: Undo2, title: "Revoke on change", body: "Policy change or dispute freezes the artifact, rebuilds downstream impact, and writes an audit event." },
  ];
  return (
    <section id="how" className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
        <div className="tracked-caps">How it works</div>
        <h2 className="mt-3 font-serif-display text-4xl md:text-5xl leading-tight max-w-3xl">
          An evidence-first workflow — from pattern to publication.
        </h2>
        <ol className="mt-14 grid md:grid-cols-5 gap-0 border border-border rounded-lg overflow-hidden">
          {steps.map((s, i) => (
            <li key={s.n} className={`p-6 bg-card ${i < steps.length - 1 ? "md:border-r border-border" : ""} ${i > 0 ? "border-t md:border-t-0" : ""} border-border`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-muted-foreground">{s.n}</span>
                <s.icon className="h-4 w-4 text-brand" />
              </div>
              <h4 className="mt-3 font-serif-display text-lg leading-tight">{s.title}</h4>
              <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function FlagshipScreens() {
  return (
    <section className="border-b border-border/80 bg-ledger/40">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24 space-y-20">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <div className="tracked-caps">Flagship</div>
            <h3 className="mt-3 font-serif-display text-3xl leading-tight">The Review Workspace</h3>
            <p className="mt-3 text-foreground/75 leading-relaxed">
              A 3-pane layout that makes it obvious: the system packages patterns, humans review them,
              and every decision is evidentiary.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-foreground/80">
              {["Source lineage with contributor class", "Editable draft with AI-assisted refinement", "Evidence + policy flags + decision log"].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-brand mt-0.5 shrink-0" /><span>{t}</span></li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-8 rounded-lg border border-border bg-card shadow-sm overflow-hidden">
            <div className="grid grid-cols-12 divide-x divide-border text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              <div className="col-span-3 p-3">Summary</div>
              <div className="col-span-6 p-3">Proposed artifact</div>
              <div className="col-span-3 p-3">Evidence · Policy · Decision</div>
            </div>
            <div className="grid grid-cols-12 divide-x divide-border h-64">
              <div className="col-span-3 p-4 space-y-2 bg-muted/30">
                <div className="h-3 w-3/4 rounded bg-border" />
                <div className="h-3 w-1/2 rounded bg-border" />
                <div className="h-3 w-2/3 rounded bg-border" />
                <div className="pt-4 h-2 w-full rounded bg-border/70" />
                <div className="h-2 w-5/6 rounded bg-border/70" />
              </div>
              <div className="col-span-6 p-4 space-y-2">
                <div className="h-3 w-2/3 rounded bg-border" />
                <div className="h-2 w-full rounded bg-border/70" />
                <div className="h-2 w-full rounded bg-border/70" />
                <div className="h-2 w-5/6 rounded bg-border/70" />
                <div className="h-2 w-4/6 rounded bg-border/70" />
                <div className="pt-4 h-2 w-full rounded bg-border/70" />
                <div className="h-2 w-3/5 rounded bg-border/70" />
              </div>
              <div className="col-span-3 p-4 space-y-2 bg-muted/30">
                <div className="h-3 w-3/5 rounded bg-border" />
                <div className="h-2 w-full rounded bg-border/70" />
                <div className="h-2 w-2/3 rounded bg-border/70" />
                <div className="mt-4 flex gap-2">
                  <div className="h-7 w-16 rounded bg-brand" />
                  <div className="h-7 w-20 rounded border border-border" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-8 rounded-lg border border-border bg-card shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="tracked-caps">Evidence Console</div>
              <span className="font-mono text-xs text-muted-foreground">EV-10177</span>
            </div>
            <h4 className="mt-2 font-serif-display text-xl">Escalation rule for payment failure after 3DS verification</h4>
            <div className="mt-6 grid md:grid-cols-2 gap-8">
              <ol className="relative border-l border-border/80 ml-2 pl-5 space-y-5">
                {[
                  ["Extracted", "02 Feb · 09:12"],
                  ["Reviewed", "04 Feb · 08:02"],
                  ["Pending approval", "—"],
                ].map(([k, v]) => (
                  <li key={k} className="relative">
                    <span className="absolute -left-[23px] top-1 h-2 w-2 rounded-full bg-brand" />
                    <div className="tracked-caps">{k}</div>
                    <div className="font-mono text-xs text-muted-foreground mt-0.5">{v}</div>
                  </li>
                ))}
              </ol>
              <dl className="space-y-3 text-sm">
                {[
                  ["Source", "Zendesk · 5 tickets"],
                  ["Policy basis", "Operational pattern, PII redaction applied"],
                  ["Review chain", "A. Cho → M. Okafor"],
                  ["Target", "Support Playbook / EU Payments Queue"],
                  ["Completeness", "65% — awaiting compliance sign-off"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-3 hairline-b pb-2">
                    <dt className="tracked-caps">{k}</dt>
                    <dd className="text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="tracked-caps">Flagship</div>
            <h3 className="mt-3 font-serif-display text-3xl leading-tight">The Evidence Console</h3>
            <p className="mt-3 text-foreground/75 leading-relaxed">
              The defensible record of how knowledge moved from source material into approved
              organizational artifacts — exportable as PDF or machine-readable JSON.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function BuyerValue() {
  return (
    <section id="buyers" className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-2 gap-8">
        {[
          {
            tag: "For support operations",
            title: "Turn recurring case patterns into approved playbooks and onboarding tools.",
            body: "Shorten time-to-competence for new agents. Reduce knowledge loss when senior operators leave. Keep your playbooks honest.",
            metrics: [["38h", "avg. time to approval"], ["74%", "candidate approval rate"], ["27", "candidates this week"]],
          },
          {
            tag: "For compliance & privacy",
            title: "Generate the evidence trail that proves human-derived knowledge in AI workflows was reviewed, approved, and can be revoked.",
            body: "Policy-aware review gates. Contributor-class labelling. Revocation with downstream impact analysis. Audit package export.",
            metrics: [["96%", "evidence completeness"], ["4", "revocations this month"], ["100%", "decisions with audit log"]],
          },
        ].map((b) => (
          <article key={b.tag} className="rounded-lg border border-border bg-card p-8">
            <div className="tracked-caps">{b.tag}</div>
            <h3 className="mt-3 font-serif-display text-3xl leading-tight">{b.title}</h3>
            <p className="mt-4 text-foreground/75 leading-relaxed">{b.body}</p>
            <div className="mt-6 grid grid-cols-3 gap-4 border-t border-border/80 pt-5">
              {b.metrics.map(([k, v]) => (
                <div key={v}>
                  <div className="font-serif-display text-2xl">{k}</div>
                  <div className="tracked-caps mt-1">{v}</div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground/50">Target benchmarks based on product design.</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function MidCTA() {
  return (
    <section className="border-b border-border/80 bg-muted/20">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-14 flex flex-col md:flex-row items-start md:items-center gap-8 justify-between">
        <div className="max-w-lg">
          <h3 className="font-serif-display text-2xl leading-snug">Ready to put your knowledge pipeline through a governed workflow?</h3>
          <p className="mt-2 text-sm text-muted-foreground">Join the waitlist — we&apos;re onboarding compliance-aware ops teams now.</p>
        </div>
        <div className="w-full md:w-auto md:min-w-[320px] shrink-0">
          <EarlyAccessForm source="landing-mid" buttonText="Request early access" />
        </div>
      </div>
    </section>
  );
}

function Architecture() {
  const commits = [
    ["No persona replication", "ProofMark never models a named individual's voice or cognition."],
    ["Human review is central", "Every publication is gated on an explicit, logged human decision."],
    ["Policy-aware extraction", "Extraction scope is bound by your active policies, not the other way around."],
    ["Revocable by design", "Every published artifact can be frozen and rolled back with downstream impact analysis."],
    ["Evidence, not opinion", "Every decision carries a source set, contributor classes, and a policy basis."],
    ["Narrow integrations", "Observation is scoped. ProofMark governs publication workflow first; runtime is optional."],
  ];
  return (
    <section id="architecture" className="border-b border-border/80 bg-ledger/40">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
        <div className="tracked-caps">Architecture commitments</div>
        <h2 className="mt-3 font-serif-display text-4xl md:text-5xl leading-tight max-w-3xl">
          Constrained by design. Defensible by default.
        </h2>
        <div className="mt-12 grid md:grid-cols-2 gap-px bg-border rounded-lg overflow-hidden">
          {commits.map(([t, b]) => (
            <div key={t} className="bg-card p-6">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-4 w-4 text-brand mt-1" />
                <div>
                  <h4 className="font-serif-display text-lg leading-snug">{t}</h4>
                  <p className="mt-1 text-sm text-foreground/75 leading-relaxed">{b}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Integrations() {
  const items = ["Zendesk", "Intercom", "Confluence", "Guru", "Notion", "Okta", "Azure AD", "Google Workspace", "Workday", "BambooHR", "AI Gateway", "CSV"];
  return (
    <section className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
        <div className="tracked-caps">Integrations</div>
        <h2 className="mt-3 font-serif-display text-4xl md:text-5xl leading-tight max-w-3xl">
          Narrow by design. Scoped to governance, not observation.
        </h2>
        <div className="mt-10 grid grid-cols-3 md:grid-cols-6 gap-3">
          {items.map((i) => (
            <div key={i} className="rounded-md border border-border bg-card px-4 py-3 text-sm text-center hover:bg-muted transition-colors">{i}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Security() {
  const items = [
    ["SOC 2 Type II", "Q4 2026"],
    ["ISO 27001", "planned"],
    ["Regional residency", "EU, US"],
    ["SSO · SCIM", "Okta, Azure AD"],
    ["Encryption", "AES-256 at rest"],
    ["Retention controls", "per policy"],
  ];
  return (
    <section id="security" className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="tracked-caps">Security &amp; governance</div>
          <h2 className="mt-3 font-serif-display text-4xl md:text-5xl leading-tight">Audit-ready by construction.</h2>
          <p className="mt-4 text-foreground/75 leading-relaxed">
            Every extraction, review, approval, and revocation writes to an immutable evidence
            record. Export as PDF or JSON for legal, privacy, or internal audit.
          </p>
        </div>
        <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-px bg-border rounded-lg overflow-hidden">
          {items.map(([k, v]) => (
            <div key={k} className="bg-card p-5">
              <div className="tracked-caps">{k}</div>
              <div className="mt-2 font-serif-display text-lg">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FREE_TOOLS = [
  {
    to: "/tools/ai-governance-maturity",
    label: "AI Governance Maturity",
    desc: "Score your programme across Classification, Review, and Governance. Level 1–5 with gap analysis.",
    time: "3 min",
  },
  {
    to: "/tools/contributor-class-classifier",
    label: "Contributor Class Classifier",
    desc: "Describe a data source and get an instant Class 1, 2, or 3 classification with review requirements.",
    time: "1 min",
  },
  {
    to: "/tools/policy-checker",
    label: "Policy Checker",
    desc: "Paste any draft rule or macro. Get AI-powered flags for PII, named-person phrasing, and compliance conflicts.",
    time: "1 min",
  },
  {
    to: "/tools/review-sla-calculator",
    label: "Review SLA Calculator",
    desc: "Input team size and weekly volume to get a realistic SLA estimate and capacity utilisation score.",
    time: "1 min",
  },
  {
    to: "/tools/revocation-risk",
    label: "Revocation Risk Score",
    desc: "Score any deployed artifact across age, source class, deployment reach, and review recency.",
    time: "1 min",
  },
];

function FreeTools() {
  return (
    <section id="tools" className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
        <div className="flex items-start justify-between gap-6 flex-wrap mb-12">
          <div>
            <div className="tracked-caps">Free tools</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-3 max-w-lg">
              Assess your governance posture — for free.
            </h2>
          </div>
          <div className="flex items-center self-end">
            <Link
              to="/tools"
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm hover:bg-muted transition-colors"
            >
              See all tools <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FREE_TOOLS.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              className="group border border-border rounded-lg p-5 bg-card hover:border-brand/50 hover:bg-muted/30 transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium group-hover:text-brand transition-colors">
                  {t.label}
                </span>
                <span className="text-xs text-muted-foreground">{t.time}</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.desc}</p>
            </Link>
          ))}

          {/* Sixth slot — CTA card */}
          <div className="border border-brand/30 rounded-lg p-5 bg-brand/5 flex flex-col justify-between">
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              These tools are built on the same model that powers ProofMark. Ready for the full
              workflow?
            </p>
            <Link
              to="/app"
              className="inline-flex items-center gap-2 text-sm font-medium text-brand hover:underline"
            >
              Open ProofMark <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  {
    q: "What is ProofMark?",
    a: "ProofMark is an enterprise platform that governs how operational knowledge enters AI systems. It provides a structured review workflow, evidence chain of custody, contributor classification, policy enforcement, and revocation — turning recurring operational patterns into approved, auditable AI behaviour.",
  },
  {
    q: "How is ProofMark different from a knowledge base?",
    a: "A knowledge base stores information. ProofMark governs what knowledge is approved to enter AI systems and on what basis. Every artifact goes through multi-stage review, policy checks, and generates an immutable evidence record. Knowledge can be revoked with full downstream impact analysis. The focus is auditability and governance, not storage.",
  },
  {
    q: "What are contributor classes?",
    a: "Contributor classes express the provenance of knowledge sources. Class 1: explicit company-owned content (policies, KB articles) — lowest scrutiny. Class 2: operational patterns derived from agent actions (support tickets, resolved cases) — requires an evidence chain. Class 3: implicit or inferred patterns — highest scrutiny. The class determines the review gates an artifact must pass before it can be deployed to an AI system.",
  },
  {
    q: "What compliance standards does ProofMark support?",
    a: "ProofMark is designed to help organisations meet the requirements of the EU AI Act, NIST AI RMF, ISO 42001, and SOC 2 AI criteria. It provides the evidence chain, review audit trail, and policy documentation these frameworks require. SOC 2 Type II certification is targeted for Q4 2026.",
  },
  {
    q: "How does revocation work?",
    a: "When an artifact needs to be revoked — due to outdated source material, a policy violation, or changed operational context — ProofMark freezes it, analyses downstream impact (which AI systems use it), triggers rebuild workflows for dependent artifacts, and logs the full event to the evidence record. Every revocation is fully auditable.",
  },
  {
    q: "How long does implementation take?",
    a: "ProofMark connects to your existing systems — Zendesk, Intercom, Confluence, Guru — via read-only integrations. Initial setup takes 1–2 days. Your first candidate artifacts surface within the first week as the system identifies recurring patterns in your resolved cases and KB edits. There is no migration and no change to your existing tools.",
  },
  {
    q: "Can AI help with artifact drafting?",
    a: "Yes. ProofMark integrates with Claude (Anthropic) to provide AI-assisted artifact refinement. Reviewers can trigger a refine pass that tightens language, removes named-person phrasing, and improves precision — with word-level diff showing exactly what changed. The AI operates within governance constraints: no first-person voice, no hype, no unverifiable claims.",
  },
];

function FAQ() {
  return (
    <section id="faq" className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
        <div className="tracked-caps mb-3">Frequently asked</div>
        <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mb-12 max-w-xl">
          Questions about ProofMark
        </h2>
        <dl className="grid md:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl">
          {FAQ_ITEMS.map(({ q, a }) => (
            <div key={q}>
              <dt className="font-medium mb-2">{q}</dt>
              <dd className="text-foreground/70 text-sm leading-relaxed">{a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section id="cta" className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24 text-center">
        <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] max-w-4xl mx-auto">
          Put your operational knowledge through a <em className="italic text-bronze">defensible</em> workflow.
        </h2>
        <p className="mt-6 text-foreground/75 max-w-xl mx-auto">
          See the dashboard, the review workspace, and the evidence console — the three screens your ops and compliance teams will live in.
        </p>

        <div className="mt-10 max-w-md mx-auto">
          <EarlyAccessForm source="landing" />
          <p className="mt-3 text-xs text-muted-foreground">No spam. Unsubscribe any time.</p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          <Link to="/app" className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-3 text-sm hover:bg-muted" data-testid="cta-open-app">
            Open the dashboard <ArrowUpRight className="h-4 w-4" />
          </Link>
          <a href="#architecture" className="inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors">
            Read the architecture brief
          </a>
        </div>
      </div>
    </section>
  );
}

function AnchorStats() {
  const stats = [
    {
      value: "6–8 weeks",
      label: "Average legal review time for an undocumented AI decision",
    },
    {
      value: "Near zero",
      label: "Enterprises with a formal knowledge artifact revocation mechanism in production",
    },
    {
      value: "Day 1",
      label: "When ProofMark starts building your audit trail",
    },
  ];
  return (
    <section className="border-b border-border/80 bg-muted/20">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-12">
        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          {stats.map((s) => (
            <div key={s.value} className="flex flex-col gap-2">
              <span className="font-serif-display text-3xl md:text-4xl">{s.value}</span>
              <span className="text-sm text-muted-foreground leading-snug">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatWeAreNot() {
  const nots = [
    {
      title: "Not an AI alignment tool",
      desc: "We don't make your model safer, smarter, or better aligned. That's a model-level problem. This is a knowledge-level problem.",
    },
    {
      title: "Not a knowledge base",
      desc: "We don't store your content. We govern what enters your AI systems — and maintain the audit trail that proves it was reviewed.",
    },
    {
      title: "Not a replacement for reviewers",
      desc: "Human decisions stay human. ProofMark structures, routes, and documents them — it doesn't make them.",
    },
  ];
  return (
    <section className="border-b border-border/80">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
        <div className="max-w-3xl">
          <div className="tracked-caps">Scope</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3 mb-6 leading-[1.05]">
            ProofMark does one thing.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {nots.map((n) => (
            <div key={n.title} className="border border-border rounded-lg p-6 bg-card">
              <p className="text-xs text-muted-foreground/60 font-mono uppercase tracking-wider mb-3">✗ {n.title}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{n.desc}</p>
            </div>
          ))}
        </div>
        <div className="border-l-4 border-brand pl-6 py-1 max-w-2xl">
          <p className="text-lg leading-relaxed">
            It makes every knowledge governance decision <strong>provable</strong>. That&apos;s the gap
            between &ldquo;we have a process&rdquo; and &ldquo;here is the audit trail.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 pt-10 pb-6 border-t border-border/80">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10 text-sm">
          <div>
            <div className="tracked-caps mb-3">Product</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="#how" className="hover:text-foreground transition-colors">How it works</a></li>
              <li><a href="#classes" className="hover:text-foreground transition-colors">Knowledge classes</a></li>
              <li><a href="#security" className="hover:text-foreground transition-colors">Security</a></li>
              <li><Link to="/app" className="hover:text-foreground transition-colors">Open app</Link></li>
            </ul>
          </div>
          <div>
            <div className="tracked-caps mb-3">Free tools</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/tools/ai-governance-maturity" className="hover:text-foreground transition-colors">Governance maturity</Link></li>
              <li><Link to="/tools/contributor-class-classifier" className="hover:text-foreground transition-colors">Contributor classifier</Link></li>
              <li><Link to="/tools/policy-checker" className="hover:text-foreground transition-colors">Policy checker</Link></li>
              <li><Link to="/tools/review-sla-calculator" className="hover:text-foreground transition-colors">SLA calculator</Link></li>
              <li><Link to="/tools/revocation-risk" className="hover:text-foreground transition-colors">Revocation risk</Link></li>
            </ul>
          </div>
          <div>
            <div className="tracked-caps mb-3">Buyers</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="#buyers" className="hover:text-foreground transition-colors">For ops leads</a></li>
              <li><a href="#buyers" className="hover:text-foreground transition-colors">For knowledge managers</a></li>
              <li><a href="#buyers" className="hover:text-foreground transition-colors">For compliance &amp; audit</a></li>
            </ul>
          </div>
          <div>
            <div className="tracked-caps mb-3">Resources</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="#faq" className="hover:text-foreground transition-colors">FAQ</a></li>
              <li><Link to="/tools" className="hover:text-foreground transition-colors">All tools</Link></li>
              <li><Link to="/glossary/ai-knowledge-artifact" className="hover:text-foreground transition-colors">What is a knowledge artifact?</Link></li>
              <li><Link to="/glossary/contributor-class" className="hover:text-foreground transition-colors">What is contributor class?</Link></li>
              <li><Link to="/glossary/ai-knowledge-governance" className="hover:text-foreground transition-colors">What is AI knowledge governance?</Link></li>
              <li><Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-6 border-t border-border/80 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <LogoMark />
            <span className="font-serif-display text-base text-foreground">ProofMark</span>
            <span>· Governed knowledge operationalization</span>
          </div>
          <div className="flex items-center gap-4">
            <span>© {new Date().getFullYear()} ProofMark. All rights reserved.</span>
            <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
